// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import * as Icon from "./OEIcons";

// ── Types ────────────────────────────────────────────────────────────────────

interface OrgPerson {
  id: string;
  company_id: string;
  name: string;
  title: string;
  phone: string;
  email: string;
  notes: string;
}

interface OrgCompany {
  id: string;
  user_id: string;
  role: string;
  company: string;
  address: string;
  phone: string;
  website: string;
  notes: string;
  contacts: OrgPerson[];
}

interface Props {
  isDarkMode: boolean;
  onClose: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

// Top-level role groups (primary dropdown)
const ROLE_GROUPS = [
  "Owner", "Owner's Rep", "Project Manager",
  "Design Team", "Trade Partner", "Inspector / AHJ", "Other",
];

// Design specialties (secondary dropdown when group === "Design Team")
const DESIGN_SUB_ROLES = [
  "Architect", "Interior Designer", "Landscape Architect", "Lighting Designer",
  "Structural Engineer", "Mechanical Engineer", "Electrical Engineer",
  "Plumbing Engineer", "Civil Engineer", "Geotechnical Engineer",
];

// Full flat list — stored values are top-level groups OR "Design Team — <Specialty>"
const DIRECTORY_ROLES = [
  "Owner", "Owner's Rep", "Project Manager",
  "Design Team",
  ...DESIGN_SUB_ROLES.map(s => `Design Team — ${s}`),
  "Trade Partner", "Inspector / AHJ", "Other",
];

const parseRole = (role: string): { group: string; sub: string } => {
  if (!role) return { group: "Trade Partner", sub: "" };
  if (role.startsWith("Design Team")) {
    const m = role.match(/^Design Team\s*[—-]\s*(.+)$/);
    return { group: "Design Team", sub: m ? m[1].trim() : "" };
  }
  if (DESIGN_SUB_ROLES.includes(role)) return { group: "Design Team", sub: role };
  return { group: role, sub: "" };
};
const joinRole = (group: string, sub: string): string => {
  if (group === "Design Team" && sub) return `Design Team — ${sub}`;
  return group;
};

const DIRECTORY_ROLE_COLOR = (role: string): string => {
  const { group, sub } = parseRole(role);
  if (group === "Design Team") {
    const subMap: Record<string, string> = {
      "Architect": "#4a90d9", "Interior Designer": "#9b6fd4",
      "Landscape Architect": "#4caf7d", "Lighting Designer": "#d4a84b",
      "Structural Engineer": "#5b8fa8", "Mechanical Engineer": "#4fd1c5",
      "Electrical Engineer": "#4fd1c5", "Plumbing Engineer": "#4fd1c5",
      "Civil Engineer": "#7cb87c", "Geotechnical Engineer": "#7cb87c",
    };
    return subMap[sub] || "#4a90d9";
  }
  const map: Record<string, string> = {
    "Owner": "#e07b3a", "Owner's Rep": "#c0604a", "Project Manager": "#c0604a",
    "Trade Partner": "#d48a4a", "Inspector / AHJ": "#8899a6", "Other": "#708898",
  };
  return map[group] || "#708898";
};

// ── CSV helpers ───────────────────────────────────────────────────────────────

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuote = !inQuote; }
    else if (ch === ',' && !inQuote) { result.push(cur); cur = ""; }
    else { cur += ch; }
  }
  result.push(cur);
  return result.map(s => s.trim().replace(/^"|"$/g, ""));
}

// Map Procore company type strings to our role labels (best-effort)
function mapProcoreType(type: string): string {
  const t = type.toLowerCase().trim();
  if (t.includes("architect") && !t.includes("landscape")) return "Design Team — Architect";
  if (t.includes("mep") || t.includes("mechanical")) return "Design Team — Mechanical Engineer";
  if (t.includes("electrical")) return "Design Team — Electrical Engineer";
  if (t.includes("plumbing")) return "Design Team — Plumbing Engineer";
  if (t.includes("structural")) return "Design Team — Structural Engineer";
  if (t.includes("civil")) return "Design Team — Civil Engineer";
  if (t.includes("geotech") || t.includes("geotechnical")) return "Design Team — Geotechnical Engineer";
  if (t.includes("landscape")) return "Design Team — Landscape Architect";
  if (t.includes("interior")) return "Design Team — Interior Designer";
  if (t.includes("lighting")) return "Design Team — Lighting Designer";
  if (t.includes("inspector") || t.includes("ahj")) return "Inspector / AHJ";
  if (t.includes("owner") && t.includes("rep")) return "Owner's Rep";
  if (t.includes("project manager") || t.includes("pm")) return "Project Manager";
  if (t.includes("owner")) return "Owner";
  if (t.includes("subcontractor") || t.includes("trade") || t.includes("contractor") ||
      t.includes("supplier") || t.includes("vendor")) return "Trade Partner";
  // Default for unknown trade categories — most Procore contacts are trades
  return "Trade Partner";
}

function uid(): string {
  return crypto.randomUUID();
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function OrgDirectory({ isDarkMode, onClose }: Props) {
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [companies, setCompanies] = useState<OrgCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  // debounce timers keyed by "<companyId>" or "<companyId>:<personId>"
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── No Supabase connection ────────────────────────────────────────────────

  if (!supabase) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: T.bgApp, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", fontFamily: FONT,
      }}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 16, left: 16,
            background: "none", border: "none", color: T.textMuted,
            cursor: "pointer", fontSize: 13, fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <Icon.ArrowLeft size={13} /> Back
        </button>
        <Icon.Lock size={32} color={T.textMuted} />
        <div style={{ marginTop: 16, fontSize: 14, color: T.textSecondary, fontWeight: 600 }}>
          Org Directory requires a Supabase connection.
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: T.textMuted }}>
          Please sign in to access the org directory.
        </div>
      </div>
    );
  }

  // ── Toast helper ──────────────────────────────────────────────────────────

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Load data ─────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    try {
      const { data: companiesData, error: cErr } = await supabase
        .from("org_directory_companies")
        .select("*")
        .order("role")
        .order("company");

      if (cErr) throw cErr;
      if (!companiesData || companiesData.length === 0) {
        setCompanies([]);
        setLoading(false);
        return;
      }

      const { data: peopleData, error: pErr } = await supabase
        .from("org_directory_people")
        .select("*")
        .in("company_id", companiesData.map((c) => c.id));

      if (pErr) throw pErr;

      const people: OrgPerson[] = peopleData || [];
      const nested: OrgCompany[] = companiesData.map((c) => ({
        ...c,
        contacts: people.filter((p) => p.company_id === c.id),
      }));

      setCompanies(nested);
    } catch (err) {
      console.error("OrgDirectory load error:", err);
      showToast("Error loading directory. Check console for details.");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── CRUD: Companies ───────────────────────────────────────────────────────

  const addCompany = async () => {
    if (!supabase) return;
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) { showToast("Not signed in."); return; }

      const { data, error } = await supabase
        .from("org_directory_companies")
        .insert({ user_id: userId, role: "Trade Partner", company: "", address: "", phone: "", website: "", notes: "" })
        .select()
        .single();

      if (error) throw error;

      const newCompany: OrgCompany = { ...data, contacts: [] };
      setCompanies((prev) => [...prev, newCompany]);
      setExpandedIds((prev) => new Set([...prev, newCompany.id]));
    } catch (err) {
      console.error("addCompany error:", err);
      showToast("Failed to add company.");
    }
  };

  const updateCompany = (id: string, patch: Partial<OrgCompany>) => {
    // Optimistic update
    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

    // Debounced Supabase sync
    const key = id;
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(async () => {
      if (!supabase) return;
      try {
        const { error } = await supabase
          .from("org_directory_companies")
          .update(patch)
          .eq("id", id);
        if (error) throw error;
      } catch (err) {
        console.error("updateCompany error:", err);
        showToast("Failed to save company changes.");
      }
    }, 300);
  };

  const deleteCompany = async (id: string) => {
    setCompanies((prev) => prev.filter((c) => c.id !== id));
    setExpandedIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    if (!supabase) return;
    try {
      const { error } = await supabase.from("org_directory_companies").delete().eq("id", id);
      if (error) throw error;
    } catch (err) {
      console.error("deleteCompany error:", err);
      showToast("Failed to delete company.");
    }
  };

  // ── CRUD: People ──────────────────────────────────────────────────────────

  const addPerson = async (companyId: string) => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from("org_directory_people")
        .insert({ company_id: companyId, name: "", title: "", phone: "", email: "", notes: "" })
        .select()
        .single();

      if (error) throw error;

      const newPerson: OrgPerson = { ...data };
      setCompanies((prev) =>
        prev.map((c) => c.id === companyId ? { ...c, contacts: [...c.contacts, newPerson] } : c)
      );
    } catch (err) {
      console.error("addPerson error:", err);
      showToast("Failed to add person.");
    }
  };

  const updatePerson = (companyId: string, personId: string, patch: Partial<OrgPerson>) => {
    // Optimistic update
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? { ...c, contacts: c.contacts.map((p) => (p.id === personId ? { ...p, ...patch } : p)) }
          : c
      )
    );

    // Debounced Supabase sync
    const key = `${companyId}:${personId}`;
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(async () => {
      if (!supabase) return;
      try {
        const { error } = await supabase
          .from("org_directory_people")
          .update(patch)
          .eq("id", personId);
        if (error) throw error;
      } catch (err) {
        console.error("updatePerson error:", err);
        showToast("Failed to save contact changes.");
      }
    }, 300);
  };

  const deletePerson = async (companyId: string, personId: string) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId ? { ...c, contacts: c.contacts.filter((p) => p.id !== personId) } : c
      )
    );
    if (!supabase) return;
    try {
      const { error } = await supabase.from("org_directory_people").delete().eq("id", personId);
      if (error) throw error;
    } catch (err) {
      console.error("deletePerson error:", err);
      showToast("Failed to delete contact.");
    }
  };

  // ── Expand toggle ─────────────────────────────────────────────────────────

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Print ─────────────────────────────────────────────────────────────────

  const printDirectory = () => {
    const grouped = DIRECTORY_ROLES.map((role) => ({
      role,
      companies: companies.filter((c) => c.role === role && (c.company || c.contacts.length)),
    })).filter((g) => g.companies.length > 0);
    const knownRoles = new Set(DIRECTORY_ROLES);
    const other = companies.filter((c) => !knownRoles.has(c.role) && (c.company || c.contacts.length));
    if (other.length) grouped.push({ role: "Other", companies: other });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Org Directory</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Inter',Arial,sans-serif;font-size:11px;color:#222;padding:32px;}
  h1{font-size:16px;font-weight:700;margin-bottom:2px;}
  .subtitle{font-size:11px;color:#666;margin-bottom:24px;}
  .role-section{margin-bottom:24px;}
  .role-title{font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
    color:#fff;background:#444;padding:4px 10px;margin-bottom:1px;}
  .company-block{margin-bottom:8px;border:1px solid #e0e0e0;border-radius:3px;overflow:hidden;}
  .company-header{background:#f7f7f7;padding:8px 12px;display:flex;gap:16px;align-items:baseline;}
  .company-name{font-weight:700;font-size:12px;}
  .company-meta{font-size:10px;color:#666;}
  table{width:100%;border-collapse:collapse;}
  th{font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;
    color:#999;padding:4px 10px;border-bottom:1px solid #eee;text-align:left;background:#fafafa;}
  td{padding:5px 10px;border-bottom:1px solid #f0f0f0;vertical-align:top;font-size:10px;}
  tr:last-child td{border-bottom:none;}
  a{color:#2980b9;text-decoration:none;}
  @media print{body{padding:16px;}@page{margin:.75in;size:letter;}}
</style></head><body>
<h1>Org Directory</h1>
<div class="subtitle">Printed ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
${grouped.map((g) => `
<div class="role-section">
  <div class="role-title">${g.role}</div>
  ${g.companies.map((co) => `
  <div class="company-block">
    <div class="company-header">
      <span class="company-name">${co.company || "(unnamed firm)"}</span>
      ${co.phone ? `<span class="company-meta"><a href="tel:${co.phone}">${co.phone}</a></span>` : ""}
      ${co.address ? `<span class="company-meta">${co.address}</span>` : ""}
      ${co.website ? `<span class="company-meta"><a href="${co.website}" target="_blank">${co.website}</a></span>` : ""}
    </div>
    ${co.contacts.length ? `
    <table>
      <thead><tr><th>Name</th><th>Title</th><th>Phone</th><th>Email</th><th>Notes</th></tr></thead>
      <tbody>${co.contacts.map((p) => `
        <tr>
          <td><strong>${p.name || "—"}</strong></td>
          <td style="color:#555">${p.title || ""}</td>
          <td>${p.phone ? `<a href="tel:${p.phone}">${p.phone}</a>` : "—"}</td>
          <td>${p.email ? `<a href="mailto:${p.email}">${p.email}</a>` : "—"}</td>
          <td style="color:#888">${p.notes || ""}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : ""}
  </div>`).join("")}
</div>`).join("")}
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  // ── Procore CSV import ────────────────────────────────────────────────────
  // Procore exports two CSV formats:
  //   Companies: "Name", "Abbreviation", "Type", "Phone", "Address 1", "City", "State", "Zip Code", "Website"
  //   Users:     "Name", "Login Email", "Company", "Business Phone", "Mobile Phone", "Job Title"

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supabase) return;
    setImporting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) { showToast("Not signed in."); return; }

      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) { showToast("CSV appears to be empty."); return; }

      const rawHeaders = parseCSVLine(lines[0]).map((h) => h.trim());
      const headers = rawHeaders.map(h => h.toLowerCase());

      // Column lookup: exact match first, then substring
      const col = (names: string[]): number => {
        for (const n of names) {
          const i = headers.findIndex(h => h === n);
          if (i >= 0) return i;
        }
        for (const n of names) {
          const i = headers.findIndex(h => h.includes(n));
          if (i >= 0) return i;
        }
        return -1;
      };

      // Detect format:
      // Users/contacts CSV has "login email", "mobile phone", or "employee id"
      const isContactsCSV =
        headers.some(h => h.includes("login email") || h.includes("mobile phone") || h.includes("employee id") || h.includes("first name")) ||
        (headers.some(h => h === "name") && headers.some(h => h === "company") && !headers.some(h => h === "abbreviation"));

      if (isContactsCSV) {
        // ── Users / Contacts CSV ──────────────────────────────────────────
        const iFullName = col(["name"]);                              // Procore "Name" (full name)
        const iFirst    = col(["first name", "firstname"]);
        const iLast     = col(["last name", "lastname"]);
        const iEmail    = col(["login email", "email address", "email"]);
        const iPhone    = col(["business phone", "mobile phone", "phone number", "phone"]);
        const iTitle    = col(["job title", "title"]);
        const iCompany  = col(["company"]);

        // Seed company map from existing
        const companyMap: Record<string, string> = {};
        for (const c of companies) companyMap[c.company.toLowerCase()] = c.id;

        const newCompanyNames: string[] = [];
        const rowsToProcess: Array<{name:string;title:string;phone:string;email:string;companyName:string}> = [];

        for (const line of lines.slice(1)) {
          if (!line.trim()) continue;
          const cols = parseCSVLine(line);

          // Build name from separate first/last fields OR a single "Name" column
          let name = "";
          if (iFirst >= 0 || iLast >= 0) {
            name = [iFirst>=0?cols[iFirst]:"", iLast>=0?cols[iLast]:""].filter(Boolean).join(" ");
          } else if (iFullName >= 0) {
            name = cols[iFullName] || "";
          }
          if (!name.trim()) continue;

          const companyName = iCompany >= 0 ? (cols[iCompany] || "").trim() : "";
          if (companyName && !(companyName.toLowerCase() in companyMap) && !newCompanyNames.includes(companyName)) {
            newCompanyNames.push(companyName);
          }
          rowsToProcess.push({
            name: name.trim(),
            title: iTitle>=0 ? cols[iTitle]||"" : "",
            phone: iPhone>=0 ? cols[iPhone]||"" : "",
            email: iEmail>=0 ? cols[iEmail]||"" : "",
            companyName,
          });
        }

        // Insert new companies (deduped)
        let newCosCount = 0;
        if (newCompanyNames.length > 0) {
          const { data: newCos, error: coErr } = await supabase
            .from("org_directory_companies")
            .insert(newCompanyNames.map(n => ({ user_id:userId, role:"Trade Partner", company:n, address:"", phone:"", website:"", notes:"" })))
            .select();
          if (coErr) throw coErr;
          for (const c of newCos||[]) companyMap[c.company.toLowerCase()] = c.id;
          newCosCount = (newCos||[]).length;
        }

        // Insert people
        const personRows = rowsToProcess
          .map(r => ({ company_id: r.companyName ? (companyMap[r.companyName.toLowerCase()]||null) : null, name:r.name, title:r.title, phone:r.phone, email:r.email, notes:"" }))
          .filter(r => r.company_id);

        let contactsCount = 0;
        if (personRows.length > 0) {
          const { data: newPeople, error: pErr } = await supabase.from("org_directory_people").insert(personRows).select();
          if (pErr) throw pErr;
          contactsCount = (newPeople||[]).length;
        }

        await loadData();
        showToast(`Imported ${newCosCount} companies and ${contactsCount} contacts`);

      } else {
        // ── Companies CSV ─────────────────────────────────────────────────
        const iName    = col(["company name", "name"]);   // Procore uses "Name"
        const iType    = col(["type", "specialty"]);
        const iPhone   = col(["phone number", "phone", "business phone"]);
        const iAddr1   = col(["address 1", "address1", "address"]);
        const iCity    = col(["city"]);
        const iState   = col(["state"]);
        const iZip     = col(["zip code", "zip", "postal"]);
        const iWebsite = col(["website"]);

        // Dedup against existing companies
        const existingNames = new Set(companies.map(c => c.company.toLowerCase()));
        const rows: Omit<OrgCompany,"id"|"contacts">[] = [];
        const seen = new Set<string>();

        for (const line of lines.slice(1)) {
          if (!line.trim()) continue;
          const cols = parseCSVLine(line);
          const companyName = (iName>=0 ? cols[iName] : "").trim();
          if (!companyName) continue;
          const key = companyName.toLowerCase();
          if (existingNames.has(key) || seen.has(key)) continue;
          seen.add(key);

          const addr = [iAddr1>=0?cols[iAddr1]:"", iCity>=0?cols[iCity]:"", iState>=0?cols[iState]:"", iZip>=0?cols[iZip]:""].filter(Boolean).join(", ");
          rows.push({
            user_id: userId,
            role:    iType>=0 ? mapProcoreType(cols[iType]||"") : "Other",
            company: companyName,
            phone:   iPhone>=0 ? cols[iPhone]||"" : "",
            website: iWebsite>=0 ? cols[iWebsite]||"" : "",
            address: addr,
            notes:   "",
          });
        }

        if (rows.length === 0) { showToast("No new companies found (all already imported)."); await loadData(); return; }

        const { data, error } = await supabase.from("org_directory_companies").insert(rows).select();
        if (error) throw error;
        await loadData();
        showToast(`Imported ${(data||[]).length} companies`);
      }
    } catch (err) {
      console.error("CSV import error:", err);
      showToast("Import failed — check browser console for details.");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  // ── Style helpers ─────────────────────────────────────────────────────────

  const inputStyle = (w?: number | string): React.CSSProperties => ({
    background: "none",
    border: "none",
    outline: "none",
    color: T.textSecondary,
    fontSize: 11,
    fontFamily: FONT,
    width: w || "100%",
  });

  const mutedInput = (w?: number | string): React.CSSProperties => ({
    ...inputStyle(w),
    color: T.textTertiary,
  });

  // ── Render ────────────────────────────────────────────────────────────────

  // Light surface palette — used for the directory rows so they read like Procore
  // regardless of whether the rest of the app is in dark or light mode.
  const LIGHT = {
    cardBg:        "#ffffff",
    headerBg:      "#f4f6f9",
    rowAltBg:      "#fafbfc",
    border:        "#dde2ea",
    borderSubtle:  "#eef1f5",
    textPrimary:   "#1f2937",
    textSecondary: "#374151",
    textMuted:     "#6b7280",
    textLabel:     "#8a93a3",
    linkBlue:      "#2563a8",
    inputBg:       "#ffffff",
    pillBg:        "#eef1f5",
    danger:        "#c0392b",
  };

  const searchQuery = search.toLowerCase().trim();
  const visibleCompanies = searchQuery
    ? companies.filter(co =>
        co.company.toLowerCase().includes(searchQuery) ||
        co.role.toLowerCase().includes(searchQuery) ||
        co.contacts.some(p => p.name.toLowerCase().includes(searchQuery) || p.email.toLowerCase().includes(searchQuery))
      )
    : companies;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: T.bgApp, display: "flex", flexDirection: "column",
      fontFamily: FONT,
    }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={handleCSVImport}
      />

      {/* Header bar */}
      <div style={{
        background: T.bgHeader,
        borderBottom: `1px solid ${T.borderPrimary}`,
        padding: "0 20px",
        height: 52,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}>
        {/* Back button */}
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none",
            color: T.textTertiary, cursor: "pointer",
            fontSize: 13, fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6,
            padding: "4px 8px", borderRadius: 6,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = T.textPrimary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = T.textTertiary; }}
        >
          <Icon.ArrowLeft size={13} /> Back
        </button>

        {/* Title */}
        <div style={{ fontSize: 16, fontWeight: 700, color: T.borderAccent, marginRight: "auto" }}>
          Org Directory
        </div>

        {/* Company count badge */}
        {companies.length > 0 && (
          <span style={{
            fontSize: 11, color: T.textMuted,
            background: T.bgAccentSubtle,
            padding: "2px 10px", borderRadius: 10,
            border: `1px solid ${T.borderAccentFaint}`,
          }}>
            {companies.length} {companies.length === 1 ? "company" : "companies"}
          </span>
        )}

        {/* Import from Procore CSV */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          style={{
            background: "none",
            border: `1px dashed ${T.borderSecondary}`,
            color: T.textMuted,
            cursor: importing ? "wait" : "pointer",
            padding: "6px 14px", borderRadius: 6,
            fontSize: 11, fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6,
            opacity: importing ? 0.5 : 1,
          }}
        >
          <Icon.Upload size={11} /> Import from Procore CSV
        </button>

        {/* Print Directory */}
        {companies.length > 0 && (
          <button
            onClick={printDirectory}
            style={{
              background: "none",
              border: `1px solid ${T.borderSecondary}`,
              color: T.textTertiary,
              cursor: "pointer",
              padding: "6px 14px", borderRadius: 6,
              fontSize: 11, fontFamily: FONT,
              display: "flex", alignItems: "center", gap: 6,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = T.borderHover; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = T.borderSecondary; }}
          >
            <Icon.Printer size={11} /> Print Directory
          </button>
        )}

        {/* Add Company */}
        <button
          onClick={addCompany}
          style={{
            background: T.borderAccent,
            border: "none",
            color: T.mode === "dark" ? "#0f1117" : "#ffffff",
            cursor: "pointer",
            padding: "7px 16px", borderRadius: 6,
            fontSize: 12, fontFamily: FONT, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <Icon.Plus size={11} /> Add Company
        </button>
      </div>

      {/* Search bar */}
      {companies.length > 0 && (
        <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", flex: 1, maxWidth: 400 }}>
            <Icon.Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textMuted, pointerEvents: "none" } as React.CSSProperties}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${companies.length} companies…`}
              style={{ width: "100%", paddingLeft: 32, paddingRight: 12, paddingTop: 7, paddingBottom: 7, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, fontSize: 12, fontFamily: FONT, color: T.textSecondary, outline: "none", boxSizing: "border-box" as const }}
            />
          </div>
          <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>
            {visibleCompanies.length} of {companies.length}
          </span>
        </div>
      )}

      {/* Body — light surface for legibility */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8, background: "#eceff3" }}>

        {/* Loading state */}
        {loading && (
          <div style={{ padding: "64px 24px", textAlign: "center", color: T.textMuted }}>
            <div style={{ fontSize: 13 }}>Loading directory…</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && companies.length === 0 && (
          <div style={{ padding: "64px 24px", textAlign: "center", color: T.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.2 }}>
              <Icon.Building size={48} color={T.textMuted} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.textWeak, marginBottom: 8 }}>
              No companies yet
            </div>
            <div style={{ fontSize: 12, maxWidth: 360, margin: "0 auto", lineHeight: 1.6 }}>
              Add firms to your org contact database — Architects, Engineers, Owners, and other parties.
              Or import directly from a Procore CSV export.
            </div>
          </div>
        )}

        {/* No-match message */}
        {!loading && companies.length > 0 && visibleCompanies.length === 0 && (
          <div style={{ padding: "40px 24px", textAlign: "center", color: T.textMuted, fontSize: 12 }}>
            No companies match &ldquo;{search}&rdquo;
          </div>
        )}

        {/* Procore-style flat list grouped by company */}
        {!loading && visibleCompanies.map((co) => {
          const expanded = expandedIds.has(co.id);
          const roleColor = DIRECTORY_ROLE_COLOR(co.role || "Other");
          const displayName = co.company.trim() || "(Unnamed company)";

          return (
            <div
              key={co.id}
              style={{
                border: `1px solid ${LIGHT.border}`,
                borderRadius: 6,
                background: LIGHT.cardBg,
                marginBottom: 0,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
            >
              {/* Company group header bar */}
              <div
                onClick={() => toggleExpanded(co.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 18px", minHeight: 56,
                  cursor: "pointer", userSelect: "none",
                  background: LIGHT.headerBg,
                  borderBottom: expanded ? `1px solid ${LIGHT.border}` : "none",
                  boxSizing: "border-box",
                }}
              >
                {/* Expand chevron */}
                <span style={{
                  color: LIGHT.textMuted, fontSize: 12, flexShrink: 0,
                  width: 14, textAlign: "center",
                  transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform .15s",
                }}>▸</span>

                {/* Role badge */}
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
                  padding: "3px 8px", borderRadius: 10,
                  background: `${roleColor}1f`, color: roleColor,
                  border: `1px solid ${roleColor}55`,
                  flexShrink: 0, whiteSpace: "nowrap", textTransform: "uppercase",
                }}>
                  {(() => { const { group, sub } = parseRole(co.role); return group === "Design Team" && sub ? sub : (group || "Other"); })()}
                </span>

                {/* Company name (Procore-style blue link) */}
                <span style={{
                  fontSize: 14, fontWeight: 700,
                  color: LIGHT.linkBlue,
                  flex: 1, minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {displayName}
                </span>

                {/* Contact count */}
                {co.contacts.length > 0 && (
                  <span style={{
                    fontSize: 11, color: LIGHT.textMuted, flexShrink: 0,
                    padding: "2px 8px", borderRadius: 10,
                    background: LIGHT.pillBg, border: `1px solid ${LIGHT.border}`,
                  }}>
                    {co.contacts.length} {co.contacts.length === 1 ? "contact" : "contacts"}
                  </span>
                )}

                {/* Office phone preview (collapsed only) */}
                {co.phone && !expanded && (
                  <span style={{ fontSize: 12, color: LIGHT.textSecondary, flexShrink: 0 }}>{co.phone}</span>
                )}

                {/* Delete company */}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteCompany(co.id); }}
                  title="Delete company"
                  style={{
                    background: "none", border: "none",
                    color: LIGHT.textMuted, cursor: "pointer",
                    padding: "4px 6px", flexShrink: 0,
                    display: "flex", alignItems: "center", opacity: 0.6,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.color = LIGHT.danger;
                    (e.currentTarget as HTMLElement).style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.color = LIGHT.textMuted;
                    (e.currentTarget as HTMLElement).style.opacity = "0.6";
                  }}
                >
                  <Icon.X size={13} />
                </button>
              </div>

              {/* Expanded body */}
              {expanded && (() => {
                const lightInput: React.CSSProperties = {
                  background: "transparent", border: "none", outline: "none",
                  color: LIGHT.textPrimary, fontSize: 12, fontFamily: FONT, width: "100%",
                };
                const lightMuted: React.CSSProperties = { ...lightInput, color: LIGHT.textSecondary };
                return (
                <div style={{ borderTop: `1px solid ${LIGHT.border}`, background: LIGHT.cardBg }}>

                  {/* Company detail fields */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 0, borderBottom: `1px solid ${LIGHT.border}`,
                    padding: "0 14px",
                  }}>
                    {[
                      {
                        label: "COMPANY NAME",
                        content: (
                          <input
                            value={co.company}
                            placeholder="Company / firm name…"
                            onChange={(e) => updateCompany(co.id, { company: e.target.value })}
                            style={{ ...lightInput, fontWeight: 600 }}
                          />
                        ),
                      },
                      {
                        label: "ROLE",
                        content: (() => {
                          const { group, sub } = parseRole(co.role);
                          const selectStyle: React.CSSProperties = {
                            background: LIGHT.inputBg,
                            border: `1px solid ${LIGHT.border}`,
                            color: roleColor,
                            padding: "3px 6px", borderRadius: 3,
                            fontSize: 10, fontFamily: FONT,
                            outline: "none", fontWeight: 700,
                          };
                          return (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <select
                                value={group}
                                onChange={(e) => updateCompany(co.id, { role: joinRole(e.target.value, e.target.value === "Design Team" ? (sub || DESIGN_SUB_ROLES[0]) : "") })}
                                style={selectStyle}
                              >
                                {ROLE_GROUPS.map((r) => <option key={r} value={r}>{r}</option>)}
                              </select>
                              {group === "Design Team" && (
                                <select
                                  value={sub || ""}
                                  onChange={(e) => updateCompany(co.id, { role: joinRole("Design Team", e.target.value) })}
                                  style={selectStyle}
                                >
                                  <option value="">Select specialty…</option>
                                  {DESIGN_SUB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                              )}
                            </div>
                          );
                        })(),
                      },
                      {
                        label: "OFFICE PHONE",
                        content: (
                          <input
                            value={co.phone}
                            placeholder="(555) 000-0000"
                            onChange={(e) => updateCompany(co.id, { phone: e.target.value })}
                            style={lightMuted}
                          />
                        ),
                      },
                      {
                        label: "WEBSITE",
                        content: (
                          <input
                            value={co.website}
                            placeholder="www.firm.com"
                            onChange={(e) => updateCompany(co.id, { website: e.target.value })}
                            style={lightMuted}
                          />
                        ),
                      },
                      {
                        label: "ADDRESS",
                        content: (
                          <input
                            value={co.address}
                            placeholder="123 Main St, City, ST 00000"
                            onChange={(e) => updateCompany(co.id, { address: e.target.value })}
                            style={lightMuted}
                          />
                        ),
                      },
                      {
                        label: "NOTES",
                        content: (
                          <input
                            value={co.notes}
                            placeholder="Notes…"
                            onChange={(e) => updateCompany(co.id, { notes: e.target.value })}
                            style={lightMuted}
                          />
                        ),
                      },
                    ].map(({ label, content }) => (
                      <div
                        key={label}
                        style={{
                          padding: "10px 0",
                          borderRight: `1px solid ${LIGHT.borderSubtle}`,
                          paddingRight: 16,
                          marginRight: 16,
                        }}
                      >
                        <div style={{
                          fontSize: 10, fontWeight: 700,
                          letterSpacing: ".1em", color: LIGHT.textLabel, marginBottom: 5,
                        }}>
                          {label}
                        </div>
                        {content}
                      </div>
                    ))}
                  </div>

                  {/* People table */}
                  {co.contacts.length > 0 && (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: LIGHT.headerBg, borderBottom: `1px solid ${LIGHT.border}` }}>
                          {["Name", "Title", "Phone", "Email", "Notes", ""].map((h) => (
                            <th key={h} style={{
                              padding: "8px 14px",
                              textAlign: "left",
                              fontSize: 10, fontWeight: 700,
                              color: LIGHT.textLabel,
                              letterSpacing: ".08em",
                              textTransform: "uppercase",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {co.contacts.map((p, idx) => (
                          <tr key={p.id} style={{
                            borderBottom: `1px solid ${LIGHT.borderSubtle}`,
                            background: idx % 2 === 0 ? LIGHT.cardBg : LIGHT.rowAltBg,
                          }}>
                            <td style={{ padding: "8px 14px", minWidth: 130 }}>
                              <input
                                value={p.name}
                                placeholder="Full name…"
                                onChange={(e) => updatePerson(co.id, p.id, { name: e.target.value })}
                                style={{ ...lightInput, fontWeight: 600 }}
                              />
                            </td>
                            <td style={{ padding: "8px 14px", minWidth: 110 }}>
                              <input
                                value={p.title}
                                placeholder="Title…"
                                onChange={(e) => updatePerson(co.id, p.id, { title: e.target.value })}
                                style={lightMuted}
                              />
                            </td>
                            <td style={{ padding: "8px 14px", minWidth: 120 }}>
                              <input
                                value={p.phone}
                                placeholder="(555) 000-0000"
                                onChange={(e) => updatePerson(co.id, p.id, { phone: e.target.value })}
                                style={lightMuted}
                              />
                            </td>
                            <td style={{ padding: "8px 14px", minWidth: 180 }}>
                              <input
                                value={p.email}
                                placeholder="email@firm.com"
                                onChange={(e) => updatePerson(co.id, p.id, { email: e.target.value })}
                                style={lightMuted}
                              />
                            </td>
                            <td style={{ padding: "8px 14px", minWidth: 140 }}>
                              <input
                                value={p.notes}
                                placeholder="Notes…"
                                onChange={(e) => updatePerson(co.id, p.id, { notes: e.target.value })}
                                style={lightMuted}
                              />
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "center" }}>
                              <button
                                onClick={() => deletePerson(co.id, p.id)}
                                style={{
                                  background: "none", border: "none",
                                  color: LIGHT.textMuted, cursor: "pointer",
                                  padding: "2px 4px", opacity: 0.6,
                                  display: "flex", alignItems: "center",
                                }}
                                onMouseEnter={(e) => {
                                  (e.currentTarget as HTMLElement).style.color = LIGHT.danger;
                                  (e.currentTarget as HTMLElement).style.opacity = "1";
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLElement).style.color = LIGHT.textMuted;
                                  (e.currentTarget as HTMLElement).style.opacity = "0.6";
                                }}
                              >
                                <Icon.X size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Add person */}
                  <div style={{
                    padding: "10px 14px",
                    borderTop: co.contacts.length ? `1px solid ${LIGHT.borderSubtle}` : "none",
                    background: LIGHT.cardBg,
                  }}>
                    <button
                      onClick={() => addPerson(co.id)}
                      style={{
                        background: "none",
                        border: `1px dashed ${LIGHT.border}`,
                        color: LIGHT.textSecondary,
                        cursor: "pointer",
                        padding: "5px 14px", borderRadius: 3,
                        fontSize: 12, fontFamily: FONT,
                        display: "flex", alignItems: "center", gap: 5,
                      }}
                    >
                      <Icon.Plus size={11} /> Add Person
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: T.bgToast,
          color: T.textInverse,
          padding: "10px 20px",
          borderRadius: 6,
          fontSize: 12,
          fontFamily: FONT,
          boxShadow: T.shadow.lg,
          border: `1px solid ${T.borderSecondary}`,
          zIndex: 400,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
